import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Prop,
} from "@stencil/core";
import api from "../../utils/api";

@Component({
    tag: "ux-iws-async-toggle-button",
    styleUrl: "async-toggle-button.scss",
    shadow: true,
})
export class ToggleButton {
    @Element() host: HTMLUxIwsAsyncToggleButtonElement;

    /** is the toggle on or off */
    @Prop({ mutable: true, reflect: true }) on = false;
    /** this id is used to identify similar toggleButtons to synchronize them */
    @Prop() readonly syncId: string = "";
    /** optional url to be called when the item should be toggled */
    @Prop() readonly toggleUrl: string = "";
    /** optional url to be called when the item should be toggled off, can be left empty if it is identical to toggleUrl */
    @Prop() readonly untoggleUrl: string = "";
    /** the component sets this to true when an error occurs while communicating with the API */
    @Prop({ mutable: true, reflect: true }) error = false;

    /** when button is clicked - returns the new toggle state as boolean */
    @Event({
        eventName: "uxIwsToggle",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    didToggle: EventEmitter<boolean>;
    /** when call to toggle url failed - returns the original toggle state as boolean */
    @Event({
        eventName: "uxIwsToggleFailed",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    toggleFailed: EventEmitter<boolean>;

    private toggle(): void {
        const { on } = this;
        this.on = !on;
        this.error = false;
        this.waitForToggle(
            () => {
                this.didToggle.emit(this.on);
                this.syncOtherButtons();
            },
            () => {
                this.on = on;
                this.error = true;
                this.toggleFailed.emit(this.on);
            }
        );
    }

    private syncOtherButtons() {
        const { syncId, on } = this;
        if (syncId === "") {
            return;
        }
        const sameButtons = document.querySelectorAll(
            `ux-iws-async-toggle-button[sync-id=${syncId}]`
        );
        [].forEach.call(
            sameButtons,
            (button: HTMLUxIwsAsyncToggleButtonElement) => {
                button.on = on;
            }
        );
    }

    private waitForToggle(callback: () => void, error: () => void) {
        const { toggleUrl, untoggleUrl, on } = this;
        if (toggleUrl === "") {
            callback();
            return;
        }
        const url = on && untoggleUrl !== "" ? untoggleUrl : toggleUrl;
        api(url).then(callback).catch(error);
    }

    render() {
        return (
            <Host
                onClick={(e: UIEvent) => {
                    e.preventDefault();
                    this.toggle();
                }}
                on={this.on}
                off={!this.on}
            >
                <slot name="on"></slot>
                <slot name="off"></slot>
            </Host>
        );
    }
}
